import { useEffect, useSyncExternalStore } from 'react'
import { OrganizationEventRegistrationsList } from './OrganizationEventRegistrationsList'
import { OrganizationEngagementList } from './OrganizationEngagementList'
import { changeEngagement, isEngagementList, useEngagementSearch } from './engagementListState'
import { useQuery } from '@tanstack/react-query'
import { ArrowUpRight } from 'lucide-react'
import { EngagementTile } from '@/components/contacts/EngagementTile'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { getJSON } from '@/lib/api'
import { displayDate } from '@/lib/dates'
import { cn } from '@/lib/utils'
import { pushLocation, subscribeLocation } from './timelineFilters'
import {
  engagementHref,
  formattedFigure,
  previousFigure,
  stateLabels,
  windowedKeys,
  type OrganizationEngagement,
} from './organizationEngagement'

export function OrganizationEngagementTab({
  contactId,
  onAdd,
  canManageAffiliations,
  hasOrganizationMembership,
}: {
  contactId: number
  onAdd: () => void
  canManageAffiliations: boolean
  hasOrganizationMembership?: boolean
}) {
  const previous = useSyncExternalStore(
    subscribeLocation,
    () => new URLSearchParams(window.location.search).get('window') === 'previous',
  )
  const query = useQuery({
    queryKey: ['organization-engagement', contactId],
    queryFn: () => getJSON<OrganizationEngagement>(`/api/v1/contacts/${contactId}/engagement`),
  })
  const selected = useSelectedList(query.data)
  if (query.isPending) return <EmptyState title="Loading engagement" variant="quiet" />
  if (query.isError) return <Alert variant="destructive">Engagement unavailable</Alert>
  const data = query.data
  const tiles = (data.headline?.tiles ?? []).filter((tile) => windowedKeys.includes(tile.key))
  const stateKeys = Object.keys(stateLabels).filter((key) =>
    data.permitted_tiles.includes(key),
  ) as (keyof typeof stateLabels)[]
  return (
    <div className="space-y-4">
      {hasOrganizationMembership && <CompanyPreviewLink contactId={contactId} />}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <WindowCaption data={data} previous={previous} />
        <div
          className="inline-flex overflow-hidden rounded-md border border-input"
          aria-label="Engagement window"
        >
          {data.window.segment_labels.map((label, index) => (
            <Button
              key={label}
              type="button"
              variant="outline"
              size="sm"
              aria-pressed={previous === (index === 1)}
              disabled={
                selected === 'personal_memberships' || (index === 1 && !data.window.whole_previous)
              }
              className={cn(
                'rounded-none border-0',
                previous === (index === 1)
                  ? 'bg-accent text-foreground'
                  : 'bg-transparent text-muted-foreground',
              )}
              onClick={() => changeEngagement({ window: index === 1 ? 'previous' : 'current' })}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>
      {data.headline?.lapsed_banner && (
        <Alert variant="warning">{data.headline.lapsed_banner}</Alert>
      )}
      <Card>
        {data.headline?.empty_message && (
          <EmptyState
            title={data.headline.empty_message}
            action={
              canManageAffiliations ? (
                <Button variant="outline" onClick={onAdd}>
                  + Add person
                </Button>
              ) : undefined
            }
          />
        )}
        {!data.headline?.empty_message && (
          <>
            <div className="grid grid-cols-2 sm:auto-cols-fr sm:grid-flow-col sm:grid-cols-none">
              {tiles.map((tile) => (
                <EngagementTile
                  key={tile.key}
                  compact
                  selected={selected === tile.key}
                  onSelect={
                    selectableListKeys.includes(tile.key)
                      ? () => changeEngagement({ tile: tile.key }, true)
                      : undefined
                  }
                  tile={
                    previous
                      ? {
                          ...tile,
                          figure: previousFigure(data.tiles[tile.key]),
                          comparison: null,
                          direction: 'none',
                          secondary_lines: [],
                        }
                      : tile
                  }
                />
              ))}
            </div>
            {stateKeys.length > 0 && (
              <div className="grid grid-cols-1 border-t border-border sm:auto-cols-fr sm:grid-flow-col sm:grid-cols-none">
                {stateKeys.map((key) => (
                  <StateTile key={key} tileKey={key} data={data} selected={selected === key} />
                ))}
              </div>
            )}
          </>
        )}
      </Card>
      <SelectedList contactId={contactId} selected={selected} window={data.window} />
    </div>
  )
}
const selectableListKeys = [...windowedKeys, 'personal_memberships']
function SelectedList({
  contactId,
  selected,
  window,
}: {
  contactId: number
  selected?: string
  window: OrganizationEngagement['window']
}) {
  if (selected === 'event_registrations')
    return <OrganizationEventRegistrationsList key={contactId} contactId={contactId} />
  return selected && isEngagementList(selected) ? (
    <OrganizationEngagementList contactId={contactId} tile={selected} window={window} />
  ) : null
}
function useSelectedList(data?: OrganizationEngagement) {
  const search = useEngagementSearch()
  const params = new URLSearchParams(search)
  const requested = params.get('tile') ?? ''
  const previous = params.get('window') === 'previous'
  const active = params.get('tab') === 'engagement'
  const available = selectableListKeys.filter(
    (key) =>
      data?.permitted_tiles.includes(key) &&
      (key === 'event_registrations' || !data.headline?.empty_message),
  )
  const selected = available.find((key) => key === requested) ?? available[0]
  useEffect(() => {
    if (
      active &&
      selected &&
      (selected !== requested || (selected === 'personal_memberships' && previous))
    )
      changeEngagement(
        { tile: selected, ...(selected === 'personal_memberships' ? { window: 'current' } : {}) },
        selected !== requested,
        'replace',
      )
  }, [selected, requested, previous, active])
  return selected
}
function StateTile({
  tileKey,
  data,
  selected,
}: {
  selected: boolean
  tileKey: keyof typeof stateLabels
  data: OrganizationEngagement
}) {
  const tile = data.tiles[tileKey]
  const content = (
    <>
      <span className="min-w-0 truncate whitespace-nowrap text-caption text-muted-foreground">
        {stateLabels[tileKey]}
      </span>
      <span className="shrink-0 text-body font-medium tabular-nums">
        {formattedFigure(tile.current)}
        {tile.capacity !== undefined && ` of ${tile.capacity}`}
      </span>
    </>
  )
  const classes =
    'grid min-w-0 grid-cols-[1fr_max-content_16px] items-center gap-2 border-border p-4 [&:not(:first-child)]:border-t sm:[&:not(:first-child)]:border-l sm:[&:not(:first-child)]:border-t-0'
  if (tileKey === 'personal_memberships')
    return (
      <Button
        variant="ghost"
        aria-pressed={selected}
        onClick={() => changeEngagement({ tile: tileKey, window: 'current' }, true)}
        className={cn(
          classes,
          'relative h-auto rounded-none text-left',
          selected &&
            'bg-primary/5 after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-primary',
        )}
      >
        {content}
        <span aria-hidden="true" />
      </Button>
    )
  return (
    <a
      className={cn(classes, 'hover:bg-accent')}
      href={engagementHref(undefined, tileKey === 'roster' ? 'membership' : 'groups')}
      onClick={(event) => {
        event.preventDefault()
        pushLocation(new URL(event.currentTarget.href))
      }}
    >
      {content}
      <ArrowUpRight aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
    </a>
  )
}

function WindowCaption({ data, previous }: { data: OrganizationEngagement; previous: boolean }) {
  if (!previous)
    return <div className="text-caption text-muted-foreground">{data.headline?.window_label}</div>
  const period = data.window.whole_previous
  const label = period
    ? `${data.window.segment_labels[1]} · ${displayDate(period.starts_on)} – ${displayDate(period.ends_on)}`
    : data.window.comparison_label
  return <div className="text-caption text-muted-foreground">{label}</div>
}

function CompanyPreviewLink({ contactId }: { contactId: number }) {
  return (
    <div className="flex justify-end">
      <Button asChild variant="link" size="sm">
        <a
          href={`/contacts/organizations/${contactId}/company-preview`}
          onClick={(event) => {
            if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
              return
            event.preventDefault()
            pushLocation(new URL(event.currentTarget.href))
          }}
        >
          Preview company view
        </a>
      </Button>
    </div>
  )
}
